import { createFileRoute, Link } from "@tanstack/react-router";
import { ORG_REF, PHYSICIAN_REF } from "@/lib/medical-schema";
import { listPosts, type PostMeta } from "@/lib/posts";
import scalpelAsset from "@/assets/scalpel-news.webp.asset.json";
import scalpelAsset256 from "@/assets/scalpel-news-256.webp.asset.json";
import { schemaEditDate, schemaIsoDate } from "@/lib/schema-dates";

const scalpelImg = scalpelAsset.url;
const scalpelImg256 = scalpelAsset256.url;

export const Route = createFileRoute("/dr-fitzer-dissects-the-news/")({
  head: () => ({
    meta: [
      { title: "Dr. Fitzer Dissects the News | Virginia Bariatric" },
      {
        name: "description",
        content:
          "Dr. Matthew Fitzer breaks down the latest bariatric surgery, weight-loss, and nutrition news.",
      },
      ...(import.meta.env.SSR ? [
        { property: "og:title", content: "Dr. Fitzer Dissects the News | Virginia Bariatric" },
        { property: "og:description", content: "Dr. Matthew Fitzer breaks down the latest bariatric surgery, weight-loss, and nutrition news." },
        { property: "og:type", content: "website" },
        { property: "og:url", content: "https://virginiabariatric.com/dr-fitzer-dissects-the-news" },
        { property: "og:image", content: "https://virginiabariatric.com/__l5e/assets-v1/64a2217d-5454-444b-b82e-298e33a87aa9/og-hero.jpg" },
        { property: "og:image:alt", content: "Dr. Fitzer Dissects the News — bariatric surgery insights from Dr. Matthew Fitzer" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:image", content: "https://virginiabariatric.com/__l5e/assets-v1/64a2217d-5454-444b-b82e-298e33a87aa9/og-hero.jpg" },
        { name: "twitter:image:alt", content: "Dr. Fitzer Dissects the News — bariatric surgery insights from Dr. Matthew Fitzer" },
        { name: "twitter:title", content: "Dr. Fitzer Dissects the News | Virginia Bariatric" },
        { name: "twitter:description", content: "Dr. Matthew Fitzer breaks down the latest bariatric surgery, weight-loss, and nutrition news." },
      ] : []),
    ],
    links: [
      // The LCP element is the first post's listing thumbnail — preload it at
      // high priority. The decorative scalpel is small and stays at auto so it
      // doesn't compete for bandwidth.
      ...(() => {
        const first = listPosts()[0];
        if (!first?.featuredImageThumb) return [];
        return [
          {
            rel: "preload",
            as: "image",
            href: first.featuredImageThumb,
            imageSrcSet: first.featuredImageThumbSrcSet,
            imageSizes: "(min-width: 640px) 224px, 100vw",
            fetchPriority: "high" as const,
          },
        ];
      })(),
      {
        rel: "preload",
        as: "image",
        href: scalpelImg256,
        imageSrcSet: `${scalpelImg256} 256w, ${scalpelImg} 512w`,
        imageSizes: "(min-width: 768px) 192px, (min-width: 640px) 160px, 128px",
      },
      { rel: "canonical", href: "https://virginiabariatric.com/dr-fitzer-dissects-the-news" },
      {
        rel: "alternate",
        hrefLang: "en",
        href: "https://virginiabariatric.com/dr-fitzer-dissects-the-news",
      },
      {
        rel: "alternate",
        hrefLang: "es",
        href: "https://virginiabariatric.com/es/el-doctor-analiza-las-noticias",
      },
      {
        rel: "alternate",
        hrefLang: "x-default",
        href: "https://virginiabariatric.com/dr-fitzer-dissects-the-news",
      },
      {
        rel: "alternate",
        type: "application/rss+xml",
        title: "Dr. Fitzer Dissects the News RSS Feed",
        href: "https://virginiabariatric.com/rss.xml",
      },
    ],
    scripts: import.meta.env.SSR ? [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Blog",
          "@id": "https://virginiabariatric.com/dr-fitzer-dissects-the-news#blog",
          ...schemaEditDate("/dr-fitzer-dissects-the-news"),
          url: "https://virginiabariatric.com/dr-fitzer-dissects-the-news",
          name: "Dr. Fitzer Dissects the News",
          description:
            "Dr. Matthew Fitzer breaks down the latest bariatric surgery, weight-loss, and nutrition news.",
          inLanguage: "en-US",
          author: {
            ...PHYSICIAN_REF,
            knowsAbout: ["Bariatric surgery", "Obesity medicine", "Weight-loss medicine"],
            worksFor: ORG_REF,
          },
          publisher: ORG_REF,
          blogPost: listPosts().map((post) => ({
            "@type": "BlogPosting",
            headline: post.title,
            description: post.seoDescription ?? post.excerpt,
            datePublished: schemaIsoDate(post.date),
            url: `https://virginiabariatric.com/dr-fitzer-dissects-the-news/${post.slug}`,
            mainEntityOfPage: {
              "@type": "WebPage",
              "@id": `https://virginiabariatric.com/dr-fitzer-dissects-the-news/${post.slug}`,
            },
            author: PHYSICIAN_REF,
          })),
        }),
      },
    ] : [],
  }),

  loader: () => ({ posts: listPosts() }),
  notFoundComponent: () => (
    <main className="mx-auto max-w-4xl px-6 md:px-12 lg:px-32 xl:px-44 2xl:px-56 py-16">
      <h1 className="text-3xl font-serif">Page not found</h1>
    </main>
  ),
  component: DrFitzerDissectsTheNewsPage,
});

function formatDate(iso: string) {
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return "";
  }
}

function DrFitzerDissectsTheNewsPage() {
  const { posts } = Route.useLoaderData();

  return (
    <main className="mx-auto max-w-4xl px-6 md:px-12 lg:px-32 xl:px-44 2xl:px-56 pt-8 pb-10">
      <header className="flex flex-col sm:flex-row items-start gap-3">
        <div className="flex-1">
          <h1 className="text-[1.9125rem] md:text-[2.55rem] font-serif leading-tight">
            Dr. Fitzer Dissects the News
          </h1>
          <p className="mt-[0.25em] sm:mt-[0.5em] text-lg text-muted-foreground max-w-2xl text-balance leading-tight">
            Dr. Fitzer writes about things going on in the professional weight-loss
            literature and popular media.
          </p>
        </div>
        <figure className="shrink-0 w-32 sm:w-40 md:w-48 mx-auto sm:ml-2 sm:mr-0">
          <img
            src={scalpelImg256}
            srcSet={`${scalpelImg256} 256w, ${scalpelImg} 512w`}
            sizes="(min-width: 768px) 192px, (min-width: 640px) 160px, 128px"
            alt="Gloved hand holding a scalpel"
            width={512}
            height={415}
            className="w-full h-auto"
            loading="eager"
            fetchPriority="high"
            decoding="async"
          />
        </figure>
      </header>

      <section className="mt-[1.8rem]">
        {posts.length === 0 ? (
          <p className="text-muted-foreground">No posts available yet.</p>
        ) : (
          <ul className="space-y-8">
            {posts.map((post: PostMeta, index: number) => (
              <li key={post.id}>
                <article className="flex flex-col sm:flex-row gap-6">
                  {post.featuredImage && (
                    <Link
                      to="/dr-fitzer-dissects-the-news/$slug"
                      params={{ slug: post.slug }}
                      className="block sm:w-56 shrink-0"
                    >
                      <img
                        src={post.featuredImageThumb ?? post.featuredImage}
                        srcSet={post.featuredImageThumbSrcSet}
                        alt={post.featuredImageAlt ?? post.title}
                        width={448}
                        height={252}
                        sizes="(min-width: 640px) 224px, 100vw"
                        className="w-full h-auto rounded-md object-cover aspect-video bg-muted"
                        loading={index === 0 ? "eager" : "lazy"}
                        fetchPriority={index === 0 ? "high" : "auto"}
                        decoding="async"
                      />

                    </Link>
                  )}
                  <div className="flex-1">
                    <h2 className="text-xl font-bold">
                      <Link
                        to="/dr-fitzer-dissects-the-news/$slug"
                        params={{ slug: post.slug }}
                        className="text-brand-logo hover:underline"
                      >
                        {post.title}
                      </Link>
                    </h2>
                    <p className="mt-1 text-sm text-muted-foreground">
                      {formatDate(post.date)}
                    </p>
                    <p className="mt-2 text-muted-foreground leading-[1.6]">
                      {post.excerpt}
                    </p>
                    <p className="mt-3">
                      <Link
                        to="/dr-fitzer-dissects-the-news/$slug"
                        params={{ slug: post.slug }}
                        className="text-brand-logo font-medium hover:underline"
                      >
                        Read: {post.title}
                      </Link>
                    </p>
                  </div>
                </article>
              </li>
            ))}
          </ul>
        )}
      </section>
    </main>
  );
}
